import { EFinancingProgram } from '@if/core/types'
import { Banner, CardGrid, ColumnDef, DataGrid, SubsectionHeader } from '@if/mui'
import { Card, CardContent } from '@mui/material'
import { useSystemFlowModes } from '@src/api/system-flow-modes-api'
import { FinancingProgramSystemFlowMode } from '@src/types/dev-tools'
import { Info } from 'lucide-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ESystemFlowModeSet, toSystemFlowModeValue } from './system-flow-mode'

interface SystemFlowModeRow extends FinancingProgramSystemFlowMode {
  id: string
}

const useSystemFlowModeColumns = (): ColumnDef<SystemFlowModeRow>[] => {
  const { t } = useTranslation()

  return useMemo<ColumnDef<SystemFlowModeRow>[]>(
    () => [
      {
        name: 'financingProgramId',
        type: 'title',
        header: t('admin.devTools.systemFlowModes.columns.financingProgram'),
        getValue: (row) => t(`EFinancingProgram.${row.financingProgramId as EFinancingProgram}`, { ns: 'enum' }),
        minWidth: 150,
        flexGrow: 1,
      },
      {
        name: 'mode',
        type: 'enumChip',
        header: t('admin.devTools.systemFlowModes.columns.mode'),
        set: ESystemFlowModeSet,
        getValue: (row) => toSystemFlowModeValue(row.mode),
        width: 130,
      },
    ],
    [t],
  )
}

const SystemFlowModesCard = () => {
  const { t } = useTranslation()
  const columns = useSystemFlowModeColumns()
  const { data, isLoading, isError } = useSystemFlowModes()

  const rows = useMemo<SystemFlowModeRow[]>(
    () =>
      (data?.financingPrograms ?? []).map((program) => ({
        ...program,
        id: program.financingProgramId,
      })),
    [data],
  )

  return (
    <Card>
      <CardGrid.Header>
        <SubsectionHeader
          icon={Info}
          title={t('admin.devTools.systemFlowModes.title')}
          subtitle={t('admin.devTools.systemFlowModes.description')}
        />
      </CardGrid.Header>
      <CardGrid.Content>
        <CardContent>
          {isError ? (
            <Banner severity="error">{t('admin.devTools.systemFlowModes.loadError')}</Banner>
          ) : (
            <DataGrid rows={rows} columns={columns} loading={isLoading} paginated={false} cardBelow={false} dense />
          )}
        </CardContent>
      </CardGrid.Content>
    </Card>
  )
}

export default SystemFlowModesCard
